export type ScaleName =
  | 'ADHD_SRS'
  | 'ASRM'
  | 'AUDIT_C'
  | 'CSSRS'
  | 'DAST10'
  | 'GAD7'
  | 'PHQ9'
  | 'YBOCS';

export enum ReasonVisitQuestionTypes {
  One = 'one',
  Many = 'many',
}

export enum SpecialtyCareTypes {
  LGBTQIA = 'I would like to see someone who specializes in LGBTQIA+ care',
  Perinatal = "I'm currently pregnant, planning to become pregnant, or have had a baby in the last year",
  Surgical = 'I need an approval letter for gender confirmation or bariatric surgery',
  None = 'None of these apply',
}

export enum V2Point1SpecialtyCareTypes {
  LGBTQIA = 'Would like to see someone who specializes in LGBTQIA+ care',
  Perinatal = 'Is currently pregnant, planning to be pregnant, or has recently had a baby',
  None = 'None of these apply',
}

export type SpecialtyCarePhrases =
  | SpecialtyCareTypes.LGBTQIA
  | SpecialtyCareTypes.Perinatal
  | SpecialtyCareTypes.Surgical
  | SpecialtyCareTypes.None;

export type V2Point1SpecialtyCarePhrases =
  | V2Point1SpecialtyCareTypes.LGBTQIA
  | V2Point1SpecialtyCareTypes.Perinatal
  | V2Point1SpecialtyCareTypes.None;

export type AlcoholUseOptions =
  | 'It feels healthy'
  | 'It feels unhealthy'
  | "I'm not sure"
  | "I don't drink alcohol";

export type V2Point1AlcoholUseOptions =
  | 'Never'
  | 'Less than once a month'
  | 'Monthly'
  | 'Weekly'
  | 'Several times per week'
  | 'Every day or almost every day';

export type DrugUseOptions =
  | 'Yes, once a week or more'
  | 'Yes, once a month or more'
  | 'Yes, once a year or more'
  | 'No, never';

export type V2Point1DrugUseOptions =
  | 'Never'
  | 'Less than once a month'
  | 'Monthly'
  | 'Weekly'
  | 'Several times per week'
  | 'Every day or almost every day';

export type V2Point1ReasonVisitKeys =
  | 'Schizophrenia or schizoaffective disorder'
  | 'Self-harm or self-injury behavior'
  | 'No, none of these';

export type V2Point1ReasonVisitAnswer = 'No, not yet';

export type ReasonVisitKey =
  | 'Depression'
  | 'Anxiety'
  | 'ADHD'
  | 'Bipolar Disorder'
  | 'Bipolar disorder' // we need to unify these between the types and the specialty entries in the db
  | 'Self-harm'
  | 'Autism'
  | 'Personality Disorder'
  | 'Schizophrenia'
  | 'Eating Disorder'
  | 'Substance Use'
  | 'LGBTQIA+'
  | 'Surgical Clearance'
  | 'Obsessive Compulsive Disorder'
  | 'Other'
  | 'ADHD'
  | 'OCD'
  | 'PTSD'
  | "I'm not sure"
  | 'Substance use disorder'
  | 'No, none of these apply'
  | 'Eating disorder'
  | 'Personality disorder'
  | SpecialtyCarePhrases
  | AlcoholUseOptions
  | DrugUseOptions
  | V2Point1ReasonVisitKeys
  | V2Point1SpecialtyCarePhrases
  | V2Point1DrugUseOptions
  | V2Point1AlcoholUseOptions
  | V2Point1ReasonVisitAnswer;

export type ReasonVisitCodeType =
  | 'RV_MainReason'
  | 'RV_SecondaryReason'
  | 'RV_SpecialtyCare'
  | 'RV_DiagnosedADHD'
  | 'RV_UseOfDrugs'
  | 'RV_DrinkingAlcohol'
  | 'RV_KillingYourself'
  | 'RV_PsychiatricImpatient';

export type ReasonVisitAnswer = 'Yes' | 'No' | "I don't know" | ReasonVisitKey;

export type ReasonVisitAnswersType = {
  [visitCode in ReasonVisitCodeType]?: ReasonVisitAnswer[];
};

export type ReasonVisitQuestion = {
  code: ReasonVisitCodeType;
  label: string;
  subLabel?: string;
  secondaryText?: string;
  type: ReasonVisitQuestionTypes;
  options?: ReasonVisitAnswer[];
  canRewrite: boolean;
  stepTitleIsNotPHI?: boolean;
};

export type ReasonVisitQuestionConfig = {
  conditionsQuestions: Array<ReasonVisitQuestion>;
  nonConditionsQuestions: Array<ReasonVisitQuestion>;
};

export type ValidatedScaleOption = {
  label: string;
  value: number;
  code: string;
};

export type PatientAgeRange = 'any' | 'juvenile' | 'adult' | 'geriatric';

export type ValidatedScalePatientType = {
  age: PatientAgeRange;
  reasonVisitAnswers: ReasonVisitAnswersType;
};

export type ValidatedScaleConfig = {
  scale: ScaleName;
  targetPatientType?: ValidatedScalePatientType;
  scoring: 'sum' | 'maximum';
  options?: ValidatedScaleOption[];
  heading?: string;
  hasStatementOptions?: boolean;
  questions: Array<{
    code: string;
    label: string;
    options?: ValidatedScaleOption[];
  }>;
  note?: string;
  // Cutoffs should always be arranged in descending order
  thresholds: Array<{
    label: string;
    cutoff: number;
  }>;
};
